import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@apollo/client/react';
import { MY_SCO_PITCH_DECKS_QUERY, REANALYZE_SCO_PITCH_DECK_MUTATION, UPSERT_SCO_PITCH_DECK_MUTATION } from '@src/components/sco/PitchDeckAuditor/gql/operations';
import { UpsertScoPitchDeckInput } from '@src/graphql-types';
import { useCallback } from 'react';
type CreateDeckOptions = {
  title?: string;
  companyId?: string;
  analyze?: boolean;
};

/**
 * Turns an uploaded `storedFileId` (from UploadDocumentButton → /assets/upload-pitch-deck)
 * into a PitchDeck and kicks the classify+audit pipeline. Also exposes reanalyze.
 */
export function usePitchDeckUpload() {
  const $ = _c(24);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      refetchQueries: [MY_SCO_PITCH_DECKS_QUERY]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [upsertDeck, upsertDeckMutation] = useMutation(UPSERT_SCO_PITCH_DECK_MUTATION, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchQueries: [MY_SCO_PITCH_DECKS_QUERY]
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [deleteDeckMutation, deleteDeckState] = useMutation(UPSERT_SCO_PITCH_DECK_MUTATION, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      refetchQueries: [MY_SCO_PITCH_DECKS_QUERY]
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [replaceFileMutation, replaceFileState] = useMutation(UPSERT_SCO_PITCH_DECK_MUTATION, t2);
  const [reanalyzeMutation, reanalyzeState] = useMutation(REANALYZE_SCO_PITCH_DECK_MUTATION);
  let t3;
  if ($[3] !== upsertDeck) {
    t3 = async (storedFileId, t4) => {
      const options = t4 === undefined ? {} : t4;
      const input = {
        storedFileId,
        title: options.title,
        companyId: options.companyId,
        analyze: options.analyze ?? true
      };
      const result = await upsertDeck({
        variables: {
          input
        }
      });
      return (result.data as any)?.upsertScoPitchDeck?.pitchDeck?.pitchDeckId ?? null;
    };
    $[3] = upsertDeck;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const createDeckFromStoredFile = t3;
  let t4;
  if ($[5] !== replaceFileMutation) {
    t4 = async (pitchDeckId, storedFileId_0, t5) => {
      const options_0 = t5 === undefined ? {} : t5;
      const input_0 = {
        pitchDeckId,
        storedFileId: storedFileId_0,
        analyze: options_0.analyze ?? true
      };
      const result_0 = await replaceFileMutation({
        variables: {
          input: input_0
        }
      });
      return (result_0.data as any)?.upsertScoPitchDeck?.pitchDeck?.pitchDeckId ?? null;
    };
    $[5] = replaceFileMutation;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const replaceDeckFile = t4;
  let t5;
  if ($[7] !== reanalyzeMutation) {
    t5 = async pitchDeckId_0 => {
      const result_1 = await reanalyzeMutation({
        variables: {
          input: {
            pitchDeckId: pitchDeckId_0
          }
        }
      });
      return (result_1.data as any)?.reanalyzeScoPitchDeck ?? null;
    };
    $[7] = reanalyzeMutation;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const reanalyze = t5;
  let t6;
  if ($[9] !== deleteDeckMutation) {
    t6 = async pitchDeckId_1 => {
      const input_1 = {
        pitchDeckId: pitchDeckId_1,
        deletedAt: new Date().toISOString()
      };
      await deleteDeckMutation({
        variables: {
          input: input_1
        },
        update: cache => {
          cache.evict({
            id: cache.identify({
              __typename: "ScoPitchDeck",
              pitchDeckId: pitchDeckId_1
            })
          });
          cache.gc();
        }
      });
    };
    $[9] = deleteDeckMutation;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const deleteDeck = t6;
  let t7;
  if ($[11] !== createDeckFromStoredFile || $[12] !== deleteDeck || $[13] !== deleteDeckState.error || $[14] !== deleteDeckState.loading || $[15] !== reanalyze || $[16] !== reanalyzeState.error || $[17] !== reanalyzeState.loading || $[18] !== replaceDeckFile || $[19] !== replaceFileState.error || $[20] !== replaceFileState.loading || $[21] !== upsertDeckMutation.error || $[22] !== upsertDeckMutation.loading) {
    t7 = {
      createDeckFromStoredFile,
      creating: upsertDeckMutation.loading,
      createError: upsertDeckMutation.error,
      replaceDeckFile,
      replacing: replaceFileState.loading,
      replaceError: replaceFileState.error,
      reanalyze,
      reanalyzing: reanalyzeState.loading,
      reanalyzeError: reanalyzeState.error,
      deleteDeck,
      deleting: deleteDeckState.loading,
      deleteError: deleteDeckState.error
    };
    $[11] = createDeckFromStoredFile;
    $[12] = deleteDeck;
    $[13] = deleteDeckState.error;
    $[14] = deleteDeckState.loading;
    $[15] = reanalyze;
    $[16] = reanalyzeState.error;
    $[17] = reanalyzeState.loading;
    $[18] = replaceDeckFile;
    $[19] = replaceFileState.error;
    $[20] = replaceFileState.loading;
    $[21] = upsertDeckMutation.error;
    $[22] = upsertDeckMutation.loading;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}